/* RESPONSIBLE TEAM: team-proactive-support */

import Service from '@ember/service';
import { inject as service } from '@ember/service';
import loadCDNScript from 'embercom/lib/load-cdn-script';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { post } from 'embercom/lib/ajax';
import { tracked } from 'tracked-built-ins';

export interface EntriDnsRecord {
  type: 'CNAME' | 'TXT';
  host: string;
  value: string;
  ttl: number;
}

export interface EntriConnectConfig {
  applicationId: string;
  token: string;
  prefilledDomain: string;
  dnsRecords: EntriDnsRecord[];
  whiteLabel: any;
  userId: number | null;
}

export const ENTRI_CONNECT_SETUP_TYPES = {
  AUTOMATIC: 'Automatic', // eslint-disable-line @intercom/intercom/no-bare-strings
  MANUAL: 'Manual', // eslint-disable-line @intercom/intercom/no-bare-strings
};

export default class EntriConnectService extends Service {
  @service appService: any;
  @tracked token = '';
  @tracked tokenExpiresAt: Date | null = null;
  @tracked link = '';
  @tracked dnsRecords: EntriDnsRecord[] = [];
  @tracked authenticationAttemptId: number | null = null;

  @task *checkDomain(dkimSettingsId: number, customBounceDomainId: number): TaskGenerator<boolean> {
    try {
      let response = yield post(
        `/ember/entri_connect/check_domain?app_id=${this.appService.app.id}`,
        {
          dkim_settings_id: dkimSettingsId,
          custom_bounce_domain_id: customBounceDomainId,
        },
      );
      this.token = response.authentication?.token;
      this.tokenExpiresAt = response.authentication?.expires_at
        ? new Date(response.authentication.expires_at)
        : null;
      this.dnsRecords = response.dns_records;
      this.authenticationAttemptId = response.authentication_attempt_id;
      let setupType = response.domain?.setup_type;
      return setupType === ENTRI_CONNECT_SETUP_TYPES.AUTOMATIC;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  @task *generateLink(
    dkimSettingsId: number,
    customBounceDomainId: number,
    dmarcRua: string | null = null,
  ): TaskGenerator<void> {
    try {
      let payload: any = {
        dkim_settings_id: dkimSettingsId,
        custom_bounce_domain_id: customBounceDomainId,
      };

      if (dmarcRua) {
        payload.dmarc_rua = dmarcRua;
      }

      let response = yield post(
        `/ember/entri_connect/generate_link?app_id=${this.appService.app.id}`,
        payload,
      );
      this.link = response.link;

      if (response.token && response.dns_records) {
        this.token = response.token;
        this.tokenExpiresAt = response.expires_at ? new Date(response.expires_at) : null;
        this.dnsRecords = response.dns_records;
      }
    } catch (error) {
      console.error(error);
      return;
    }
  }

  @task *recordAppAdminEvent(domain: string): TaskGenerator<void> {
    try {
      yield post(`/ember/entri_connect/record_event?app_id=${this.appService.app.id}`, {
        domain,
        authentication_attempt_id: this.authenticationAttemptId,
      });
    } catch (error) {
      console.error(error);
      return;
    }
  }

  @task *loadEntriLibrary(): TaskGenerator<any> {
    try {
      if ((window as any).entri === undefined) {
        return yield loadCDNScript(this.librarySourceScript);
      }
    } catch (error) {
      console.error(error);
    }
  }

  get librarySourceScript() {
    return `https://cdn.goentri.com/entri.js`;
  }

  // https://developers.entri.com/api-reference#whitelabel-object
  get whitelabelOptions(): any {
    return {
      hideEntriLogo: true,
      hideProgressIndicator: true,
      removeShareLogin: true,
      theme: {
        bg: '#222222', // button background color
        fg: '#ffffff', // button text color
        links: '#334bfa',
        buttons: {
          border: {
            radius: '8px',
          },
        },
        googleFont: {
          // eslint-disable-next-line @intercom/intercom/no-bare-strings
          family: 'Inter',
          variant: '500',
        },
      },
      customProperties: {
        initialScreen: {
          disableScreen: true,
        },
        manualConfiguration: {
          disableScreen: true,
        },
        providerLogin: {
          showEntriToS: false,
          disableExtendedLoginAnimation: true,
          forwardLink: {
            style: [],
          },
          gotoManualLink: {
            disable: true,
          },
        },
      },
    };
  }
}
